/**
 * GameState.ts — the single serializable state object plus a factory for a new
 * game. Systems mutate their own namespace in place. Everything here is plain
 * data (no methods, no class instances) so it serializes cleanly.
 *
 * Derived values (effective production rates, energy balance, multipliers) are
 * NEVER stored here — they are recomputed by systems on every tick and on load,
 * which prevents save-tampering and migration drift.
 */
import { createRng, type RngState } from './rng';
import { BALANCE, DEFAULT_SEED, MAX_TOOL_LEVEL, ORES } from '../data';

/** Current save schema version. Bump when the shape changes; add a migration. */
export const SAVE_VERSION = 4;

export interface ActiveModifier {
  /** Source id (event id, quest boost, etc.). */
  id: string;
  label: string;
  effect: import('../data').UpgradeEffect;
  /** Game-time (ms) at which this modifier expires; Infinity = permanent. */
  endsAt: number;
}

export interface ActiveQuest {
  id: string;
  oreId: string;
  amount: number;
  reward: number;
  researchReward: number;
}

export interface GameState {
  version: number;
  /** Serialized RNG so the simulation is fully reproducible. */
  rng: RngState;

  meta: {
    createdAt: number;
    /** Wall-clock ms of the last save (used for offline progress). */
    lastSavedAt: number;
    /** Accumulated in-game time in ms (advances one tick at a time). */
    gameTimeMs: number;
    /** Total real playtime in ms. */
    playtimeMs: number;
  };

  economy: {
    money: number;
  };

  mining: {
    /** The layer the player is actively focused on. */
    focusDepth: number;
    /** Deepest layer unlocked. */
    maxDepth: number;
    /** Current pickaxe/drill tier (1..MAX_TOOL_LEVEL). */
    toolLevel: number;
    /** Raw ore inventory by ore id. */
    ore: Record<string, number>;
    /** Refined ingot inventory by ore id. */
    ingots: Record<string, number>;
  };

  buildings: Record<string, number>;
  workers: Record<string, number>;
  upgrades: Record<string, boolean>;

  market: {
    /** Current price per raw unit by ore id (mean-reverts around baseValue). */
    price: Record<string, number>;
    nextUpdateAt: number;
  };

  research: {
    points: number;
    researched: Record<string, boolean>;
  };

  prestige: {
    points: number;
    totalEarned: number;
    resets: number;
    skills: Record<string, number>;
  };

  modifiers: ActiveModifier[];

  events: {
    activeId: string | null;
    nextRollAt: number;
    cooldownUntil: number;
  };

  quests: {
    active: ActiveQuest[];
    completed: string[];
    nextOfferAt: number;
  };

  achievements: Record<string, boolean>;

  stats: {
    lifetimeEarnings: number;
    totalOreMined: number;
    totalClicks: number;
    oreMined: Record<string, number>;
    oreSold: Record<string, number>;
    nuggetsClicked: number;
    abilitiesUsed: number;
    maxCombo: number;
  };

  settings: {
    muted: boolean;
    notation: NumberNotation;
    autosell: boolean;
    masterVolume: number;
    musicVolume: number;
    sfxVolume: number;
    musicEnabled: boolean;
    theme: string;
    unlockedThemes: string[];
    reducedMotion: boolean;
    particleDensity: number;
    fontScale: number;
  };

  /** First-run onboarding: spotlight tutorial progress + next-goal tracker. */
  onboarding: {
    tutorialStep: number;
    tutorialDone: boolean;
    goalIndex: number;
    dismissed: boolean;
  };

  /** Active-play: click combo (decays by time window). */
  combo: {
    count: number;
    lastClickAt: number;
  };

  /** Activatable abilities: id -> gameTimeMs when off cooldown. */
  abilities: Record<string, { readyAt: number }>;

  /** Golden nugget lifecycle (x/y normalized 0..1). */
  nugget: {
    active: boolean;
    kind: string;
    x: number;
    y: number;
    spawnedAt: number;
    expiresAt: number;
    nextSpawnAt: number;
  };

  /** Ascension: the second prestige layer. */
  ascension: {
    cores: number;
    totalCores: number;
    ascensions: number;
    perks: Record<string, number>;
  };

  /** Daily-login bonus. */
  daily: {
    lastClaimDay: number;
    streak: number;
  };

  /** Automation / managers (each flag off by default, unlocked by progress). */
  automation: {
    autoNugget: boolean;
    autoBuy: boolean;
    autoPrestige: boolean;
    autoAscend: boolean;
    autoPrestigeThreshold: number;
    autoAscendThreshold: number;
    lastAutoBuyAt: number;
  };

  /** Optional run challenges. */
  challenges: {
    activeId: string | null;
    /** lifetimeEarnings when the active challenge started (for "earn X during" goals). */
    startLife: number;
    completed: string[];
  };
}

export type NumberNotation = 'short' | 'scientific' | 'engineering' | 'letters';

/** Build a fresh game state. */
export function newGame(now: number, seed = DEFAULT_SEED): GameState {
  const oreZero = (): Record<string, number> =>
    Object.fromEntries(ORES.map((o) => [o.id, 0]));

  return {
    version: SAVE_VERSION,
    rng: createRng(seed),
    meta: {
      createdAt: now,
      lastSavedAt: now,
      gameTimeMs: 0,
      playtimeMs: 0,
    },
    economy: {
      money: BALANCE.startMoney,
    },
    mining: {
      focusDepth: 0,
      maxDepth: 0,
      toolLevel: 1,
      ore: oreZero(),
      ingots: oreZero(),
    },
    buildings: {},
    workers: {},
    upgrades: {},
    market: {
      price: Object.fromEntries(ORES.map((o) => [o.id, o.baseValue])),
      nextUpdateAt: 0,
    },
    research: {
      points: 0,
      researched: {},
    },
    prestige: {
      points: 0,
      totalEarned: 0,
      resets: 0,
      skills: {},
    },
    modifiers: [],
    events: {
      activeId: null,
      nextRollAt: 0,
      cooldownUntil: 0,
    },
    quests: {
      active: [],
      completed: [],
      nextOfferAt: 0,
    },
    achievements: {},
    stats: {
      lifetimeEarnings: 0,
      totalOreMined: 0,
      totalClicks: 0,
      oreMined: oreZero(),
      oreSold: oreZero(),
      nuggetsClicked: 0,
      abilitiesUsed: 0,
      maxCombo: 0,
    },
    settings: {
      muted: false,
      notation: 'short',
      autosell: false,
      masterVolume: 0.8,
      musicVolume: 0.5,
      sfxVolume: 0.8,
      musicEnabled: true,
      theme: 'default',
      unlockedThemes: ['default'],
      reducedMotion: false,
      particleDensity: 1,
      fontScale: 1,
    },
    onboarding: {
      tutorialStep: 0,
      tutorialDone: false,
      goalIndex: 0,
      dismissed: false,
    },
    combo: { count: 0, lastClickAt: 0 },
    abilities: {},
    nugget: { active: false, kind: '', x: 0, y: 0, spawnedAt: 0, expiresAt: 0, nextSpawnAt: 0 },
    ascension: { cores: 0, totalCores: 0, ascensions: 0, perks: {} },
    daily: { lastClaimDay: 0, streak: 0 },
    automation: {
      autoNugget: false,
      autoBuy: false,
      autoPrestige: false,
      autoAscend: false,
      autoPrestigeThreshold: 100,
      autoAscendThreshold: 10,
      lastAutoBuyAt: 0,
    },
    challenges: { activeId: null, startLife: 0, completed: [] },
  };
}

/** Clamp the tool level into valid range (used after migrations/load). */
export function clampToolLevel(level: number): number {
  return Math.max(1, Math.min(MAX_TOOL_LEVEL, Math.floor(level)));
}
